import type { AreaHighlight, FeatureStory, SpecCard } from '@/types'

import {
    BedDouble,
    Car,
    Flame,
    Footprints,
    GraduationCap,
    House,
    Mountain,
    ShieldCheck,
    ShoppingBag,
    Sofa,
    Sprout,
    Stethoscope
} from 'lucide-react'

// Headline facts shown under the hero
export const heroFacts = [
    { value: "14", label: "Freestanding homes" },
    { value: "3", label: "Bedrooms each" },
    { value: "1", label: "Level, no stairs" },
    { value: "2", label: "Car garage" }
]

// Property spec cards in the Residences section
export const specCards: Array<SpecCard> = [
    {
        title: "Three bedrooms",
        description: "Room for family, guests or a quiet study, with every bedroom on the same level as the living areas.",
        icon: BedDouble
    },
    {
        title: "Single-storey living",
        description: "Every room on one level, from the front door to the garden. No stairs to climb, now or later.",
        icon: Footprints
    },
    {
        title: "Freestanding home",
        description: "No shared walls. Each home stands on its own, with privacy on every side.",
        icon: House
    },
    {
        title: "Open-plan living",
        description: "Kitchen, dining and lounge flow together as one generous, light-filled space.",
        icon: Sofa
    },
    {
        title: "Built-in fireplace",
        description: "A fireplace at the heart of the living area for cool, misty Hillcrest evenings.",
        icon: Flame
    },
    {
        title: "Level garden",
        description: "A private, level garden that is easy to enjoy and easy to keep.",
        icon: Sprout
    },
    {
        title: "Double garage",
        description: "Secure parking for two cars, with space to spare for storage.",
        icon: Car
    },
    {
        title: "Gated estate",
        description: "Fourteen homes behind one secure gate, with visitors pre-registered by residents.",
        icon: ShieldCheck
    }
]

// Alternating image and text stories that follow the spec cards
export const featureStories: Array<FeatureStory> = [
    {
        id: "open-plan-living",
        eyebrow: "Open-plan living",
        title: "Made for long lunches and easy evenings",
        body: "Kitchen, dining and lounge share one generous, light-filled space that flows towards the garden, so cooking, conversation and the view never sit in separate rooms.",
        points: [
            "Kitchen, dining and lounge in one flowing space",
            "Living areas that open towards the garden",
            "Every room on a single, step-free level"
        ],
        image: "/images/open-plan-living.jpg",
        imageAlt: "Open-plan kitchen, dining area and lounge with a fireplace, opening onto a level garden"
    },
    {
        id: "fireplace",
        eyebrow: "Built-in fireplace",
        title: "Warmth at the heart of the home",
        body: "When the Hillcrest mist rolls in, a built-in fireplace turns the living room into the place everyone wants to be. Quietly crafted, and made to be used, not just admired.",
        points: [
            "A built-in fireplace in the living area",
            "A calm, natural palette throughout",
            "Designed for slow evenings at home"
        ],
        image: "/images/fireplace-lounge.jpg",
        imageAlt: "Calm living room with a built-in log niche, linen sofa and natural timber table"
    },
    {
        id: "level-garden",
        eyebrow: "Level gardens",
        title: "A garden you simply step into",
        body: "Every home has its own private, level garden. No slopes and no steps, just a flat lawn and planting that are as easy to enjoy at seventy as they are at forty.",
        points: [
            "A private, level garden for every home",
            "Easy to keep, lovely to live with",
            "Space for a table in the sun"
        ],
        image: "/images/level-garden.jpg",
        imageAlt: "Covered patio with an outdoor dining table opening onto a flat lawn and lush indigenous planting"
    }
]

// Right-sized living benefits for down-scalers
export const lifestyleBenefits = [
    "No stairs, now or ever",
    "Lock up and go with real peace of mind",
    "A garden you enjoy, rather than one that keeps you busy",
    "Three bedrooms, so family can still come to stay",
    "Doctors, shops and friends all close by"
]

// Area highlights for central Hillcrest and Hilldene
export const areaHighlights: Array<AreaHighlight> = [
    {
        title: "Medical care",
        summary: "Peace of mind, close to home.",
        icon: Stethoscope,
        places: [
            "Busamed Hillcrest Private Hospital, with a 24-hour emergency unit",
            "Medicross Hillcrest medical centre",
            "GPs, specialists and pharmacies along Old Main Road"
        ]
    },
    {
        title: "Schools",
        summary: "Some of KwaZulu-Natal's most respected schools.",
        icon: GraduationCap,
        places: [
            "Highbury Preparatory School",
            "Kearsney College",
            "Hillcrest Primary and Hillcrest High",
            "Thomas More College",
            "St Mary's DSG, Kloof"
        ]
    },
    {
        title: "Shopping",
        summary: "Everyday essentials and village favourites.",
        icon: ShoppingBag,
        places: [
            "Hillcrest Corner, the area's largest centre",
            "Lillies Quarter, with Woolworths Food and Clicks",
            "Christians Village",
            "Oxford Village and its fresh market",
            "Watercrest Mall in neighbouring Waterfall"
        ]
    },
    {
        title: "Outdoors and leisure",
        summary: "Green space, views and easy weekends.",
        icon: Mountain,
        places: [
            "The Valley of a Thousand Hills",
            "Krantzkloof Nature Reserve",
            "Village cafés and restaurants on Old Main Road",
            "Quick access to the M13 and N3"
        ]
    }
]

// Options for the private viewing form
export const buyerProfiles = [
    "Down-scaling from a larger home",
    "Moving up to a freestanding home",
    "Relocating to Hillcrest",
    "Buying as an investment",
    "Something else"
]

export const viewingTimes = [
    "Weekday morning",
    "Weekday afternoon",
    "Saturday morning",
    "I'm flexible"
]
